import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useRadio } from "@/context/RadioContext";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import { storage } from "@/lib/storage";
import { INSTALL_NUDGE, visitDays } from "@/lib/engagement";
import PushToggle from "@/components/PushToggle";

const DISMISS_KEY = "atlas.installDismissed";
const RESHOW_MS = 14 * 86400000;
const MIN_VISIT_DAYS = 2;
const dismissedRecently = () => Date.now() - Number(storage.get(DISMISS_KEY) || 0) < RESHOW_MS;
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const SLIDE_UP = { initial: { y: 80, opacity: 0 }, animate: { y: 0, opacity: 1 }, exit: { y: 80, opacity: 0 }, transition: { type: "spring", stiffness: 260, damping: 24 } };

export default function InstallBanner() {
  const { t } = useLang();
  const radio = useRadio();
  const { pathname } = useLocation();
  const lifted = Boolean(radio?.current) && pathname !== "/radio";
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  // Smart timing: only from the 2nd visit day on, or right after finishing the Daily Challenge (INSTALL_NUDGE).
  useEffect(() => {
    if (isStandalone() || dismissedRecently() || window.location.search.includes("nobanner")) return;
    let nudged = false;
    const eligible = () => nudged || visitDays() >= MIN_VISIT_DAYS;
    const reveal = () => { if (eligible()) { setShow(true); trackEvent("install_banner_shown", { reason: nudged ? "daily_challenge" : "returning_visit" }); } };
    const onNudge = () => { nudged = true; reveal(); };
    window.addEventListener(INSTALL_NUDGE, onNudge);
    if (isIos()) {
      setIos(true);
      const timer = setTimeout(reveal, 4000);
      return () => { clearTimeout(timer); window.removeEventListener(INSTALL_NUDGE, onNudge); };
    }
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); reveal(); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => { setShow(false); trackEvent("app_installed"); });
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener(INSTALL_NUDGE, onNudge); };
  }, []);

  const dismiss = () => {
    storage.set(DISMISS_KEY, String(Date.now()));
    setShow(false);
    trackEvent("install_dismissed");
  };

  const install = async () => {
    if (!prompt) return;
    trackEvent("install_prompt_shown");
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    trackEvent("install_choice", { outcome });
    setPrompt(null);
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (ios || prompt) && (
        <motion.div
          {...SLIDE_UP}
          className={`fixed ${lifted ? "bottom-40 md:bottom-24" : "bottom-20 md:bottom-4"} inset-x-4 sm:inset-x-auto sm:end-6 sm:w-96 z-50 rounded-2xl border border-stoneLine bg-sand/95 backdrop-blur-xl shadow-xl p-4 flex items-start gap-3`}
          data-testid="install-banner"
        >
          <img src="/icons/icon-192.png" alt="" className="w-12 h-12 rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-ink text-sm">{t("install_title")}</div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
              {ios ? t("install_ios_hint") : t("install_text")}
            </p>
            {ios ? (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-ochre font-medium">
                <Share className="w-3.5 h-3.5" /> → {t("install_ios_action")}
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button
                  onClick={install}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-sand text-xs font-medium hover:bg-ochre transition-colors"
                  data-testid="install-button"
                >
                  <Download className="w-3.5 h-3.5" /> {t("install_action")}
                </button>
                <PushToggle compact />
              </div>
            )}
          </div>
          <button onClick={dismiss} aria-label="Close" className="text-ink/50 hover:text-ink shrink-0" data-testid="install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
